/**
 * Gráficos del Inicio contable (SVG propio, como `dashboard/charts.tsx`).
 *
 * Paleta de dos series validada con el validador de dataviz (superficie
 * clara; el admin no tiene modo oscuro): ingresos #3A6FB0, gastos #B07A2E.
 * Un solo eje en pesos. Los meses provisorios van con trama y rotulados
 * "prov." (el color nunca es el único portador de la información). Cada marca
 * lleva `<title>` como tooltip y cada gráfico tiene su tabla equivalente.
 */
import { Link } from "react-router-dom";

import { CHART_COLORS } from "../../lib/chartColors";
import { pesos, pesosCortos } from "../../lib/format";
import type { MesResumen } from "./api";

export const COLOR_INGRESOS = "#3A6FB0";
export const COLOR_GASTOS = "#B07A2E";
// Tinta y grilla salen de los tokens comunes (antes eran grises `stone` de
// Tailwind, fuera de la paleta del ecosistema).
const TINTA_SECUNDARIA = CHART_COLORS.neutral600;
const GRILLA = CHART_COLORS.neutral200;

const W = 960;
const H = 250;
const PAD_L = 84;
const PAD_R = 12;
const PAD_T = 12;
const PAD_B = 44;

function escala(max: number, min = 0, alto = H): (v: number) => number {
  const span = max - min || 1;
  return (v) => PAD_T + (alto - PAD_T - PAD_B) * (1 - (v - min) / span);
}

function ticks(min: number, max: number): number[] {
  const span = max - min;
  if (span <= 0) return [0];
  return [0, 0.25, 0.5, 0.75, 1].map((f) => min + span * f);
}

function EjeY({
  min,
  max,
  y,
  ancho = W,
}: {
  min: number;
  max: number;
  y: (v: number) => number;
  ancho?: number;
}): JSX.Element {
  return (
    <g>
      {ticks(min, max).map((t) => (
        <g key={t}>
          <line x1={PAD_L} x2={ancho - PAD_R} y1={y(t)} y2={y(t)} stroke={GRILLA} strokeWidth={1} />
          <text x={PAD_L - 8} y={y(t) + 4} fontSize={11} textAnchor="end" fill={TINTA_SECUNDARIA}>
            {pesosCortos(Math.round(t))}
          </text>
        </g>
      ))}
    </g>
  );
}

function Trama({ id, color }: { id: string; color: string }): JSX.Element {
  return (
    <pattern id={id} patternUnits="userSpaceOnUse" width={6} height={6} patternTransform="rotate(45)">
      <rect width={6} height={6} fill={color} fillOpacity={0.35} />
      <line x1={0} y1={0} x2={0} y2={6} stroke={color} strokeWidth={3} />
    </pattern>
  );
}

export function Leyenda({ items }: { items: { color: string; texto: string }[] }): JSX.Element {
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-600">
      {items.map((i) => (
        <li key={i.texto} className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ background: i.color }} aria-hidden="true" />
          {i.texto}
        </li>
      ))}
    </ul>
  );
}

/** Ingresos vs gastos por mes (barras agrupadas, un eje). */
export function IngresosGastos({ meses }: { meses: MesResumen[] }): JSX.Element {
  const max = Math.max(1, ...meses.flatMap((m) => [m.ingresos, m.gastos]));
  const y = escala(max);
  const banda = (W - PAD_L - PAD_R) / Math.max(meses.length, 1);
  const barra = Math.min(28, (banda - 10) / 2);
  const base = y(0);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" role="img" aria-label="Ingresos y gastos por mes">
      <defs>
        <Trama id="trama-ing" color={COLOR_INGRESOS} />
        <Trama id="trama-gas" color={COLOR_GASTOS} />
      </defs>
      <EjeY min={0} max={max} y={y} />
      {meses.map((m, i) => {
        const x0 = PAD_L + banda * i + (banda - (barra * 2 + 2)) / 2;
        const barras = [
          { v: m.ingresos, color: COLOR_INGRESOS, trama: "trama-ing", nombre: "Ingresos", x: x0 },
          { v: m.gastos, color: COLOR_GASTOS, trama: "trama-gas", nombre: "Gastos", x: x0 + barra + 2 },
        ];
        return (
          <g key={`${m.anio}-${m.mes}`}>
            {barras.map((b) => {
              const alto = Math.max(base - y(b.v), b.v > 0 ? 1 : 0);
              return (
                <rect
                  key={b.nombre}
                  x={b.x}
                  y={base - alto}
                  width={barra}
                  height={alto}
                  rx={3}
                  fill={m.provisorio ? `url(#${b.trama})` : b.color}
                >
                  <title>
                    {`${m.etiqueta}${m.provisorio ? " (provisorio)" : ""} · ${b.nombre}: ${pesos(b.v)}`}
                  </title>
                </rect>
              );
            })}
            {/* Hit target de todo el mes con el resumen completo. */}
            <rect x={PAD_L + banda * i} y={PAD_T} width={banda} height={H - PAD_T - PAD_B} fill="transparent">
              <title>
                {`${m.etiqueta}${m.provisorio ? " (provisorio)" : ""}\nIngresos: ${pesos(m.ingresos)}\nGastos: ${pesos(m.gastos)}\nResultado: ${pesos(m.resultado)}`}
              </title>
            </rect>
            <text
              x={PAD_L + banda * i + banda / 2}
              y={H - PAD_B + 18}
              fontSize={11}
              textAnchor="middle"
              fill={TINTA_SECUNDARIA}
            >
              {m.etiqueta.split(" ")[0]}
            </text>
            {m.provisorio && (
              <text
                x={PAD_L + banda * i + banda / 2}
                y={H - PAD_B + 32}
                fontSize={10}
                textAnchor="middle"
                fill={TINTA_SECUNDARIA}
              >
                prov.
              </text>
            )}
          </g>
        );
      })}
      <line x1={PAD_L} x2={W - PAD_R} y1={base} y2={base} stroke={CHART_COLORS.neutral450} strokeWidth={1} />
    </svg>
  );
}

/** Evolución del disponible (una serie: sin leyenda, el título la nombra). */
export function EvolucionDisponible({ meses }: { meses: MesResumen[] }): JSX.Element {
  const valores = meses.map((m) => m.disponible);
  const min = Math.min(0, ...valores);
  const max = Math.max(1, ...valores);
  const alto = H + 40;
  const y = escala(max, min, alto);
  const ancho = 640;
  const paso = meses.length > 1 ? (ancho - PAD_L - PAD_R - 20) / (meses.length - 1) : 0;
  const puntos = meses.map((m, i) => ({ x: PAD_L + 10 + paso * i, y: y(m.disponible), m }));
  const trazo = puntos.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${ancho} ${alto}`} className="h-auto w-full" role="img" aria-label="Disponible al cierre de cada mes">
      <EjeY min={min} max={max} y={y} ancho={ancho} />
      <path d={trazo} fill="none" stroke={COLOR_INGRESOS} strokeWidth={2} strokeLinejoin="round" />
      {puntos.map((p) => (
        <g key={`${p.m.anio}-${p.m.mes}`}>
          <circle
            cx={p.x}
            cy={p.y}
            r={4.5}
            fill={p.m.provisorio ? "#fff" : COLOR_INGRESOS}
            stroke={COLOR_INGRESOS}
            strokeWidth={2}
          />
          <circle cx={p.x} cy={p.y} r={14} fill="transparent">
            <title>{`${p.m.etiqueta}${p.m.provisorio ? " (provisorio)" : ""}: ${pesos(p.m.disponible)}`}</title>
          </circle>
          <text x={p.x} y={alto - PAD_B + 18} fontSize={11} textAnchor="middle" fill={TINTA_SECUNDARIA}>
            {p.m.etiqueta.split(" ")[0]}
          </text>
        </g>
      ))}
    </svg>
  );
}

/** Barras horizontales (gastos por rubro), con el monto rotulado. */
export function BarrasHorizontales({
  items,
}: {
  items: { clave: string; etiqueta: string; valor: number; href?: string | undefined }[];
}): JSX.Element {
  const max = Math.max(1, ...items.map((i) => i.valor));
  return (
    <ul className="space-y-2">
      {items.map((i) => {
        const ancho = `${Math.max(1, (i.valor / max) * 100)}%`;
        const contenido = (
          <>
            <span className="flex items-baseline justify-between gap-3 text-sm">
              <span className="truncate text-neutral-800">{i.etiqueta}</span>
              <span className="shrink-0 tabular-nums text-neutral-900">{pesos(i.valor)}</span>
            </span>
            <span className="mt-1 block h-2 rounded-sm bg-neutral-100" aria-hidden="true">
              <span className="block h-2 rounded-sm" style={{ width: ancho, background: COLOR_GASTOS }} />
            </span>
          </>
        );
        return (
          <li key={i.clave}>
            {i.href ? (
              <Link
                to={i.href}
                className="block rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 hover:[&_span.truncate]:text-brand-700"
              >
                {contenido}
              </Link>
            ) : (
              contenido
            )}
          </li>
        );
      })}
    </ul>
  );
}
